import {
  ApiErrorResponseBodyProps,
  deleteApiOptions,
  postApiOptions,
  putApiOptions,
} from 'config/api'
import { setGlobal } from 'reactn'
import isError, { IsErrorProps } from 'ui/utils/isError'

const responseHasJsonBody: (response: Response) => boolean = (response) => {
  const contentType = response.headers.get('content-type')
  return !!(contentType && contentType.indexOf('application/json') !== -1)
}

export const searchParams = (params: { [key: string]: any }) => {
  if (params) {
    const p = new URLSearchParams()
    Object.keys(params).forEach((key) => {
      params[key] !== undefined && p.append(key, params[key])
    })
    return p.toString()
  }
  return ''
}

export class ApiErrorWithNoMessage extends Error {
  readonly response: Response

  constructor(response: Response) {
    super()
    this.name = new.target.name
    this.response = response
  }
}

export class NotFoundError extends Error {
  constructor() {
    super()
    this.name = new.target.name
  }
}

export class OfflineError extends Error {
  constructor(e?: string) {
    super(e)
    this.name = new.target.name
  }
}

export class TimeoutError extends Error {
  constructor(e?: string) {
    super(e)
    this.name = new.target.name
  }
}

export class NetworkError extends Error {
  constructor(e?: string) {
    super(e)
    this.name = new.target.name
  }
}

export class Api401Error extends Error {
  constructor() {
    super()
    this.name = new.target.name
    setGlobal({ user: undefined })
  }
}

export class Api403Error extends Error {
  constructor() {
    super()
    this.name = new.target.name
    setGlobal({ error: 'forbidden' })
  }
}

export class ApiErrorWithMessage extends Error {
  readonly body: ApiErrorResponseBodyProps
  readonly response: Response

  constructor(response: Response, data: ApiErrorResponseBodyProps) {
    super()
    this.name = new.target.name
    this.body = data
    this.response = response
  }

  has(props: IsErrorProps): boolean {
    return isError(this.body, props)
  }
}

const DEFAULT_TIMEOUT_MS = 120 * 1000

export const fetchWithTimeout: (
  input: RequestInfo,
  init?: RequestInit,
  timeout?: number
) => Promise<Response | unknown> = (
  url,
  options,
  timeout = DEFAULT_TIMEOUT_MS
) =>
  Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new TimeoutError()), timeout)
    ),
  ])

/**
 * <T>に指定した型のjsonでPromiseを返すclient
 *
 * @param url
 * @param params
 * @param method
 * @param fetchApi
 * @throws Response
 */
export const client: <T>(
  url: string,
  params?: object,
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE',
  fetchApi?: (
    input: RequestInfo,
    init?: RequestInit
  ) => Promise<Response | unknown>
) => Promise<T> = async (url, params, method = 'GET', fetchApi = fetch) => {
  let response: Response | unknown
  switch (method) {
    case 'GET':
      response = await fetchApi(params ? `${url}?${searchParams(params)}` : url)
      break
    case 'POST':
      response = await fetchApi(url, postApiOptions(params || {}))
      break
    case 'PUT':
      response = await fetchApi(url, putApiOptions(params || {}))
      break
    case 'DELETE':
      response = await fetchApi(url, deleteApiOptions())
      break
  }

  if (response instanceof Response) {
    const hasJsonBody = responseHasJsonBody(response)
    if (response.ok) {
      return hasJsonBody ? response.json() : null
    } else {
      if (response.status === 404) {
        throw new NotFoundError()
      }
      if (response.status === 401) {
        throw new Api401Error()
      }
      if (response.status === 403) {
        throw new Api403Error()
      }
      if (response.status === 404) {
        throw new NotFoundError()
      }
      if (hasJsonBody) {
        const body = response.json()
        return body.then((data) => {
          throw new ApiErrorWithMessage(response as Response, data)
        })
      }
      throw new ApiErrorWithNoMessage(response)
    }
  } else {
    throw new NetworkError()
  }
}
